import { Download, Edit } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Button, CircularProgress, LinearProgress, Slide, Tooltip, Typography } from '@mui/material';
import { t } from 'i18next';
import { debounce, isEmpty, toNumber } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DataTable, { SortOrder, TableColumn } from 'react-data-table-component';
import { useDispatch } from 'react-redux';
import { useAuth } from '../../hooks/useAuth';
import { useExam } from '../../hooks/useExam';
import { TAnalyticData } from '../../pages/analytics';
import { AnalyticsData } from '../../shared/types/analyticData.type';
import { TAnalyticParams } from '../../shared/types/analyticParameters.type';
import { TAnalyticView } from '../../shared/types/analyticView.type';
import { setEditTrainingsModal, setTrainingsToEdit } from '../../store/reducers/modal.reducer';
import RenderTooltip from './RenderTooltip';

type DatatableProps = {
  analyticsData?: AnalyticsData,
  view: TAnalyticView | null,
  loading: boolean,
  updateFilter: (key: keyof TAnalyticData, value: any) => void,
  updateName: (name: string) => void,
  toggleDeleteModal: (value: boolean) => void,
  openNewParamsModal: () => void,
  isDeleting: boolean,
  isNameUpdating: boolean,
  isEditEnabled: boolean,
  selectedParams: TAnalyticParams[],
  updateSelectedParams: (items: TAnalyticParams[]) => void,
  disableEdit?: boolean,
  moreThan1000: boolean,


}

function Datatable({ moreThan1000, disableEdit, isEditEnabled, updateSelectedParams, selectedParams, analyticsData, view, loading, updateFilter, updateName, toggleDeleteModal, isDeleting, isNameUpdating, openNewParamsModal }: DatatableProps) {



  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(100)
  const [showEdit, setShowEdit] = useState(false)
  const [selectedRows, setSelectedRows] = useState<number[]>([])
  const stylesRef = React.useRef<HTMLStyleElement[]>([]);
  const [render, setRender] = useState(false)
  const { auth } = useAuth()
  const {
    exportXlsxData,
    isExportingXlsx
  } = useExam(false, null)
  //Hooks
  const dispatch = useDispatch()

  const xlsxEnabled = useMemo(() => {
    if (!auth) return false

    const XSLX_CONFIG_KEY = "ALLOW_XLSX_MOMENTS_EXPORT"

    const getConfig = auth.configurations.find(conf => conf.key === XSLX_CONFIG_KEY)

    if (!getConfig) return false

    return getConfig.value === 'true'

  }, [auth])


  const columnStyle = (index: number = 0): any => {

    return {
      position: 'sticky',
      left: `${index * 100}px`, // Adjust 'left' based on column index
      zIndex: 1,
      backgroundColor: '#222222 !important',
    }
  }

  const removeStyles = () => {
    // Anything in here is fired on component unmount.
    // Clear previously created styles on view change
    try {
      stylesRef.current.forEach(styleElement => {
        document.head.removeChild(styleElement);
      });
      stylesRef.current = []; // Clear the reference array
    } catch (error) {

    } finally {
      setRender(!render)
    }
  }


  // Function to create a new CSS class
  function createCSSClass(className: string, styles: any) {

    const style = document.createElement('style');
    style.type = 'text/css';

    // Create the CSS rule
    style.innerHTML = `${className} { ${styles} }`;

    // Append the style element to the head
    document.head.appendChild(style);
    return style
  }

  const rows = useMemo(() => {
    let data = []

    if (analyticsData?.averages) {
      analyticsData.averages[0] = t('firstSection:average').concat(' ').concat(moreThan1000 ? `(${t('limitedSample')})` : '')
      data.push(analyticsData.averages.map(e => e ?? '-'))
    }
    if (analyticsData?.data) {
      data.push(...analyticsData.data)
    }
    return data
  }, [analyticsData, moreThan1000])


  const columns = useMemo((): TableColumn<string[]>[] => {
    removeStyles()
    if (!view) return []
    let stickyCols = view.count_columns_locked

    stylesRef.current.push(createCSSClass('.rdt_TableCol:nth-child(2)', "position: sticky;left: 0;z-index: 9;background-color: #222222 !important"))
    stylesRef.current.push(createCSSClass('.rdt_TableCol:nth-child(3)', "position: sticky;left: 100px;z-index: 9;background-color: #222222 !important"))

    return [
      {
        name: t('eap:table:headers:date'),
        sortable: true,
        style: columnStyle(0),
        center: true,
        compact: true,
        id: `${0}`,
        cell: (row, i) => {
          return <div key={`row-0-cell-${i}`} style={{ textAlign: 'center' }} dangerouslySetInnerHTML={{ __html: row[0] }}></div>
        }
      },
      {
        name: t('eap:table:headers:horse'),
        sortable: true,
        style: columnStyle(1),
        center: true,
        id: `${1}`,
        compact: true,
        cell: (row, i) => <RenderTooltip key={row[1]} element={row[1]} />
      },
      ...selectedParams.map((e, index) => {

        let isSticky = stickyCols >= index + 1

        if (isSticky) {
          stylesRef.current.push(createCSSClass(`.rdt_TableCol:nth-child(${4 + index})`, `position: sticky;left: ${(index + 2) * 100}px;z-index: 9;background-color: #222222`))

        }

        return {
          name: <Tooltip title={e.details_translated} arrow placement='top'><Box display={'flex'} flexDirection={'column'} alignItems={'center'} justifyContent={'center'} textAlign={'center'} >
            <Typography variant='caption'>{e.name_translated}</Typography>
            <Typography variant='caption'>{e.unit_translated && `(${e.unit_translated})`}</Typography>
          </Box></Tooltip>,
          style: ((stickyCols >= index + 1) ? columnStyle(index + 2) : {}),
          center: true,
          compact: true,
          id: `${index + 2}`,
          insight_param: e,
          reorder: !isSticky,
          sortable: true,
          cell: (row: string[], i: number) => <div key={`row-${index + 2}-cell-${i}`} style={{ textAlign: 'center' }} dangerouslySetInnerHTML={{ __html: row[index + 2] }}></div>,
        }
      })]
  }, [selectedParams, rows.length, view?.id, view?.count_columns_locked])




  useEffect(() => {
    setShowEdit(false)
  }, [view?.id])



  useEffect(() => {
    let header_row = document.querySelector('.rdt_TableHead')

    if (header_row && analyticsData?.averages) {

      try {
        let row = document.querySelector('#row-0') as HTMLDivElement

        if (row) {
          row.style.top = header_row.clientHeight + 'px'
          row.classList.add('sticky_row')
          document.querySelector('#row-0 > div input')?.remove()
        }
        const divElements = document.querySelectorAll<HTMLDivElement>('#row-0 .rdt_TableCell');

        divElements.forEach(element => {
          element.style.setProperty('background-color', '#9b141a', 'important');
        })
      } catch (error) {
        console.warn('Error while styling avg row')
      }

    }
  }, [analyticsData?.averages, view?.id])

  //Fns
  const handleSort = useCallback(async (column: TableColumn<string[]>, dir: SortOrder) => {
    /// reach out to some API and get new data using or sortField and sortDirection
    // e.g. https://api.github.com/search/repositories?q=blog&sort=${column.sortField}&order=${sortDirection}
    updateFilter('order', {
      dir,
      column: toNumber(column.id)
    })

  }, []);
  const paginationChanged = async (currentRowsPerPage: number, currentPage: number) => {
    //Function fired when we changes the length of the rows per page
    setPageSize(currentRowsPerPage)
    setCurrentPage(currentPage)

    updateFilter('length', currentRowsPerPage === (analyticsData?.records_filtered ?? 0) ? -1 : currentRowsPerPage)
  };
  const onChangePage = async (currentPage: number, totalRows: number) => {
    //Function fired when we naviagte between pages
    setCurrentPage(currentPage)
    updateFilter('start', (currentPage * pageSize) - pageSize)
  };


  const handleRowSelected = React.useCallback((state: {
    allSelected: boolean,
    selectedCount: number,
    selectedRows: string[][]
  }) => {
    if (isEmpty(state.selectedRows)) {
      setSelectedRows([])
      setShowEdit(false)
      return
    }
    let spans = state.selectedRows.map(e => e[0])


    //We get the id for data attribute with regex
    let ids: number[] = []
    spans.forEach(span => {

      const match = span.match(/data-training-id='(\d+)'/);

      if (match) {
        const trainingId = match[1];
        ids.push(toNumber(trainingId))
      }

    })




    setSelectedRows(ids)
    setShowEdit(true)
  }, []);

  const openEditModal = () => {
    dispatch(setTrainingsToEdit(selectedRows))
    dispatch(setEditTrainingsModal(true))
  }

  const handleColsChange = debounce((cols: any[]) => {

    //We remove the 2 first cols that are static
    let removedStaticCols = cols.slice(2, cols.length - 1).map(c => c.insight_param) as TAnalyticParams[]
    updateSelectedParams(removedStaticCols)

  }, 3000)

  const downloadXlsx = useCallback(() => {
    exportXlsxData(selectedRows)

  }, [selectedRows])

  return (
    <>
      {
        !disableEdit && <Slide direction="left" in={showEdit} mountOnEnter unmountOnExit>
          <Box sx={{ overflow: 'hidden' }} display={'flex'} justifyContent={'end'} my={2} gap={2}>
            <Button onClick={openEditModal} startIcon={<Edit></Edit>} variant='contained'>{t('analytics:edit')}</Button>
            {xlsxEnabled && <LoadingButton loading={isExportingXlsx} onClick={downloadXlsx} startIcon={<Download></Download>} variant='contained'>{t('report:exportXlsx')}</LoadingButton>}

          </Box>
        </Slide>
      }

      {
        loading && <LinearProgress />
      }
      <div className='custom-analytics-table'>
        <DataTable
          keyField="id"
          style={{ overflowX: "auto", position: 'relative' }}

          fixedHeader
          columns={columns}
          data={rows}
          theme='dark'
          selectableRows

          onSelectedRowsChange={handleRowSelected}
          sortServer
          pagination
          paginationServer
          progressPending={loading}
          paginationTotalRows={analyticsData?.records_filtered}
          onSort={handleSort}
          fixedHeaderScrollHeight={'75vh'}
          progressComponent={<CircularProgress sx={{ my: 3 }}></CircularProgress>}
          onChangeRowsPerPage={paginationChanged}
          onChangePage={onChangePage}
          paginationRowsPerPageOptions={[10, 25, 50, 75, 100, analyticsData?.records_filtered ?? 0]}
          paginationPerPage={pageSize}
          onColumnOrderChange={handleColsChange}
          paginationComponentOptions={{

            rangeSeparatorText: t('of'),
            rowsPerPageText: t('eap:table:rowPerPage')
          }}
        />
      </div>
    </>
  )
}

export default Datatable